import {
  AIWorkflow,
  WorkflowStepValues,
  SystemEntity,
  type WorkflowStep,
} from "@erp/shared-types";
import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * Workflow Step schema for tracking where notes are used
 */
export const WorkflowStepSchema: z.ZodType<WorkflowStep> = z.enum(
  // Flatten mapping to a single literal array once, keeping a single source of truth
  WorkflowStepValues as [WorkflowStep, ...WorkflowStep[]],
);

/**
 * Memory Bank Note metadata schema. Used for vector search, and very flexible.
 * Feel free to add more fields as needed, or not.
 */
export const MemoryBankNoteMetadataSchema = z
  .object({
    workflow: z.nativeEnum(AIWorkflow).optional(),
    workflowStep: WorkflowStepSchema.optional(),
    customerEmailDomain: z.string().optional(),
  })
  .catchall(
    // matches to VectorSearchService's VectorUpsertMetadata type
    z.union([
      z.string(),
      z.number(),
      z.boolean(),
      z.array(z.string()),
      z.undefined(),
    ]),
  );

export const MemoryBankSupportedEntityTypesSchema = z.enum([
  SystemEntity.AR_CUSTOMER,
  SystemEntity.AR_STAGED_SALES_ORDER,
  SystemEntity.AP_INVOICE,
]);

/**
 * Edit Log Action schema
 */
export const EditLogActionSchema = z.enum([
  "created",
  "edited",
  "optimisticEdit",
  "rollbackOptimisticEdit",
  "confirmOptimisticEdit",
  "deleted",
]);

/**
 * Edit Log Entry schema
 */
export const EditLogEntrySchema = z.object({
  action: EditLogActionSchema,
  timestamp: z.string(),
  noteContent: z.string(),
  workflowStep: WorkflowStepSchema.optional(),
  version: z.int().min(0).optional(), // Version when the note was used (0 = created but not used)
  actor: z.object({
    type: z.enum(["user", "system"]),
    userId: z.string().optional(),
    userName: z.string().optional(),
    model: z.string().optional(),
    systemComponent: z.string().optional(),
  }),
  metadata: z
    .object({
      reason: z.string().optional(),
      previousNoteContent: z.string().optional(),
    })
    .catchall(z.unknown())
    .optional(),
});

/**
 * Memory Bank Note schema
 */
export const MemoryBankNoteSchema = z.object({
  id: z.uuid(),
  note: z.string(),
  metadata: MemoryBankNoteMetadataSchema.optional(),
  editLog: z.array(EditLogEntrySchema),
  verifiedAt: z.coerce.date(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  deletedAt: z.coerce.date().optional(),
});

/**
 * Create Note Input Schema
 */
export const CreateNoteInputSchema = z.object({
  note: z.string().min(1).max(10000),
  metadata: MemoryBankNoteMetadataSchema.optional(),
  entityId: z.uuid().optional(),
  entityType: MemoryBankSupportedEntityTypesSchema.optional(),
});

/**
 * Update Note Input Schema
 */
export const UpdateNoteInputSchema = z
  .object({
    note: z.string().min(1).max(10000).optional(),
    metadata: MemoryBankNoteMetadataSchema.optional(),
    editLogContext: z
      .object({
        actorType: z.enum(["user", "system"]),
        userId: z.string().optional(),
        userName: z.string().optional(),
        model: z.string().optional(),
        systemComponent: z.string().optional(),
        reason: z.string().optional(),
      })
      .optional(),
  })
  .refine((data) => data.note !== undefined || data.metadata !== undefined, {
    error: "At least one field (note or metadata) must be provided",
  });

/**
 * List Notes Input Schema
 */
export const ListNotesInputSchema = z.object({
  limit: z.int().min(1).max(100).prefault(20),
  offset: z.int().min(0).prefault(0),
  workflow: z.string().optional(),
  workflowStep: WorkflowStepSchema.optional(),
  search: z.string().optional(),
});

/**
 * List Notes Response Schema
 */
export const ListNotesResponseSchema = z.object({
  notes: z.array(MemoryBankNoteSchema),
  pagination: z.object({
    total: z.number(),
    limit: z.number(),
    offset: z.number(),
    hasMore: z.boolean(),
  }),
});

/**
 * Search Notes Input Schema
 */
export const SearchNotesInputSchema = z.object({
  query: z.string().min(1).max(1000),
  minMatch: z.number().min(0).max(1).prefault(0.6),
  limit: z.int().min(1).max(50).prefault(10),
  strict: z.boolean().prefault(false),
  requiredMetadata: MemoryBankNoteMetadataSchema.optional(),
});

/**
 * Relevant Note Result Schema
 */
export const RelevantNoteResultSchema = z.object({
  note: MemoryBankNoteSchema,
  confidence: z.number(),
  matchedOn: z.string().optional(),
});

/**
 * Search Notes Response Schema
 */
export const SearchNotesResponseSchema = z.object({
  query: z.string(),
  results: z.array(RelevantNoteResultSchema),
  searchOptions: z.object({
    minMatch: z.number(),
    limit: z.number(),
    strict: z.boolean(),
  }),
});

/**
 * Stats Response Schema
 */
export const StatsResponseSchema = z.object({
  totalNotes: z.number(),
  vectorIndexStats: z.unknown(),
});

/**
 * AI Improve Note Input Schema
 */
export const ImproveNoteInputSchema = z.object({
  noteId: z.uuid(),
  context: z.string().optional(), // Optional context to help the AI improve the note
});

/**
 * AI Improve Note Response Schema
 */
export const ImproveNoteResponseSchema = z.object({
  originalNote: z.string(),
  improvedNote: z.string(),
  improvements: z.array(z.string()), // List of specific improvements made
  confidence: z.number().min(0).max(1), // AI confidence in the improvements
  model: z.string(), // Model used to generate the improvements
});

/**
 * Note Usage Schema for the new polymorphic usage table
 */
export const NoteUsageSchema = z.object({
  noteId: z.uuid(),
  workflow: z.nativeEnum(AIWorkflow).optional(),
  workflowStep: WorkflowStepSchema.optional(),
  version: z.int().min(0),
  usageMetadata: z.record(z.string(), z.unknown()).optional(),
  createdAt: z.coerce.date(),
});

/**
 * List Note Usages Input Schema
 */
export const ListNoteUsagesInputSchema = z.object({
  entityType: MemoryBankSupportedEntityTypesSchema,
  entityId: z.uuid(),
});

/**
 * List Note Usages Response Schema
 */
export const ListNoteUsagesResponseSchema = z.object({
  usages: z.array(NoteUsageSchema),
});

/**
 * Memory Bank Note Contracts
 */
export const memoryBankContracts = {
  // Create a new note
  create: oc.input(CreateNoteInputSchema).output(MemoryBankNoteSchema).route({
    method: "POST",
  }),

  // List notes with filtering and pagination
  list: oc.input(ListNotesInputSchema).output(ListNotesResponseSchema).route({
    method: "GET",
  }),

  // Get a specific note by ID
  get: oc
    .input(z.object({ id: z.uuid() }))
    .output(MemoryBankNoteSchema.nullable())
    .route({
      method: "GET",
    }),

  // Batch get multiple notes by IDs
  batchGet: oc
    .input(z.object({ ids: z.array(z.uuid()).min(1).max(100) }))
    .output(z.array(MemoryBankNoteSchema))
    .route({
      method: "POST",
    }),

  // Update a note
  update: oc
    .input(
      z.object({
        id: z.uuid(),
        data: UpdateNoteInputSchema,
      }),
    )
    .output(MemoryBankNoteSchema.nullable())
    .route({
      method: "PUT",
    }),

  // Delete (archive) a note
  delete: oc
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ success: z.boolean(), message: z.string() }))
    .route({
      method: "DELETE",
    }),

  // Search notes with vector similarity
  search: oc
    .input(SearchNotesInputSchema)
    .output(SearchNotesResponseSchema)
    .route({
      method: "POST",
    }),

  // Get service statistics
  stats: oc.output(StatsResponseSchema).route({
    method: "GET",
  }),

  // Get AI improvement suggestions for a note
  getImproveNoteSuggestion: oc
    .input(ImproveNoteInputSchema)
    .output(ImproveNoteResponseSchema)
    .route({
      method: "GET",
    }),

  // List note usages for an entity
  listUsages: oc
    .input(ListNoteUsagesInputSchema)
    .output(ListNoteUsagesResponseSchema)
    .route({
      method: "GET",
    }),
} as const;

// Export types
export type MemoryBankNoteMetadata = z.infer<
  typeof MemoryBankNoteMetadataSchema
>;
export type EditLogAction = z.infer<typeof EditLogActionSchema>;
export type EditLogEntry = z.infer<typeof EditLogEntrySchema>;
export type MemoryBankNote = z.infer<typeof MemoryBankNoteSchema>;
export type CreateNoteInput = z.infer<typeof CreateNoteInputSchema>;
export type UpdateNoteInput = z.infer<typeof UpdateNoteInputSchema>;
export type ListNotesInput = z.infer<typeof ListNotesInputSchema>;
export type ListNotesResponse = z.infer<typeof ListNotesResponseSchema>;
export type SearchNotesInput = z.infer<typeof SearchNotesInputSchema>;
export type SearchNotesResponse = z.infer<typeof SearchNotesResponseSchema>;
export type RelevantNoteResult = z.infer<typeof RelevantNoteResultSchema>;
export type StatsResponse = z.infer<typeof StatsResponseSchema>;
export type ImproveNoteInput = z.infer<typeof ImproveNoteInputSchema>;
export type ImproveNoteResponse = z.infer<typeof ImproveNoteResponseSchema>;
export type ListNoteUsagesInput = z.infer<typeof ListNoteUsagesInputSchema>;
export type ListNoteUsagesResponse = z.infer<
  typeof ListNoteUsagesResponseSchema
>;
export type MemoryBankSupportedEntityTypes = z.infer<
  typeof MemoryBankSupportedEntityTypesSchema
>;
export type NoteUsage = z.infer<typeof NoteUsageSchema>;
